import { MethodType } from '@piccolohealth/pbs-common';
import { ChakraV3, DescriptionList } from '@piccolohealth/ui';
import { DateTime, P } from '@piccolohealth/util';
import type { TopLevelFormatterParams } from 'echarts/types/dist/shared.js';
import React from 'react';
import { UserDescription } from '../../../../components/user/UserDescription';
import { ChakraProvider } from '../../../../theme/chakra';
import { formatDurationValue, type RecordingUserData } from '../../utils';

interface Props {
  params: TopLevelFormatterParams;
  users: RecordingUserData;
}

export const ChartTooltip = (props: Props) => {
  const flatParams = P.flatten([props.params]);

  const first = flatParams[0];

  const data = first?.data as Record<string, number | string> | undefined;
  const timestamp = data?.timestamp as string | undefined;

  const items = React.useMemo(() => {
    return flatParams.flatMap((item) => {
      const data = item.data as Record<string, number | string>;
      const marker = item.marker as string;

      const seriesName = item.seriesName;
      const seriesIndex = item.seriesIndex;

      if (P.isNil(seriesName) || P.isNil(seriesIndex) || P.isUndefined(timestamp)) {
        return [];
      }

      const seriesId = Object.keys(data ?? {})[seriesIndex + 1];
      const seriesValue = Object.values(data ?? {})[seriesIndex + 1];

      if (P.isNil(seriesId) || P.isNil(seriesValue)) {
        return [];
      }

      const users = props.users[timestamp]?.[seriesId] ?? [];
      const isDuration = seriesId.startsWith(MethodType.Duration);
      // Non number values are already formatted. I.e. abc data
      const formattedValue =
        isDuration && typeof seriesValue === 'number'
          ? formatDurationValue(seriesValue)
          : seriesValue;

      return {
        marker,
        seriesName,
        seriesValue: formattedValue,
        users,
      };
    });
  }, [flatParams, props.users, timestamp]);

  const users = React.useMemo(() => {
    return P.uniqBy(
      items.flatMap((item) => item.users),
      (u) => u.email,
    );
  }, [items]);

  if (P.isUndefined(first) || P.isEmpty(items) || P.isUndefined(timestamp)) {
    return null;
  }

  const formattedTimestamp = DateTime.fromISO(timestamp).toLocaleString(DateTime.DATE_MED);

  return (
    <ChakraProvider>
      <ChakraV3.Stack>
        <ChakraV3.Text fontWeight='semibold'>{formattedTimestamp}</ChakraV3.Text>
        <DescriptionList
          items={items.map(({ marker, seriesName, seriesValue }) => ({
            title: (
              <ChakraV3.HStack>
                {/** biome-ignore lint/security/noDangerouslySetInnerHtml: legitimate usage */}
                <ChakraV3.Stack dangerouslySetInnerHTML={{ __html: marker }} />
                <ChakraV3.Text color='secondary'>{seriesName}</ChakraV3.Text>
              </ChakraV3.HStack>
            ),
            content: <ChakraV3.Text fontWeight='semibold'>{seriesValue}</ChakraV3.Text>,
          }))}
          rowGap={0}
        />
        {!P.isEmpty(users) && (
          <>
            <ChakraV3.Separator />
            <ChakraV3.Stack>
              <ChakraV3.Text fontWeight='semibold' color='secondary'>
                Recorded by
              </ChakraV3.Text>
              {users.map((user) => (
                <UserDescription
                  key={user.email}
                  name={user.name}
                  secondary={user.email}
                  picture={user.__typename === 'User' ? user.picture : undefined}
                />
              ))}
            </ChakraV3.Stack>
          </>
        )}
      </ChakraV3.Stack>
    </ChakraProvider>
  );
};
